import { useState, useEffect } from "react";
import { X, Shield, Lock, Activity, Package, User as UserIcon, Save, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import type { UserAccessItem } from "../../../api/accessApi";
import {
  updateAccessUserStatus,
  updateAccessUserRole,
  updateAccessUserOverrides,
  updateAccessUserPlan,
} from "../../../api/accessApi";

interface UserDrawerProps {
  user: UserAccessItem | null;
  isOpen: boolean;
  onClose: () => void;
  onUserUpdated: () => void;
  currentAdminEmail?: string;
}

const ALL_FEATURES = [
  { id: "dashboard", label: "Dashboard", desc: "Main threat intelligence overview" },
  { id: "intelligence_feed", label: "Intelligence Feed", desc: "Darkweb breach & leak search" },
  { id: "timeline", label: "Timeline", desc: "Historical breach events graph" },
  { id: "customer_check", label: "Customer Check", desc: "Domain & user leak checks" },
  { id: "domain_monitor", label: "Domain Monitor", desc: "Domain watchlist & scanner" },
  { id: "alert_config", label: "Alert Config", desc: "Webhooks & instant alerts" },
  { id: "takedowns", label: "Takedowns", desc: "Takedown requests & tracking" },
  { id: "review", label: "Review", desc: "Sample uploads & malware analysis" },
  { id: "subscription", label: "Subscription", desc: "Pricing & tier upgrades" },
  { id: "admin_dashboard", label: "Admin Dashboard", desc: "Admin stats & scraper jobs" },
  { id: "access_management", label: "Access Console", desc: "User & role management" },
  { id: "audit_logs", label: "Audit Logs", desc: "System activity audit trail" },
  { id: "forum_monitoring", label: "Forum Monitoring", desc: "Scraper status & errors" },
  { id: "reports_export", label: "Reports & CSV", desc: "Export report downloads" },
  { id: "api_access", label: "API Access", desc: "REST API keys & tokens" },
];

export default function UserDrawer({
  user,
  isOpen,
  onClose,
  onUserUpdated,
  currentAdminEmail,
}: UserDrawerProps) {
  const [activeTab, setActiveTab] = useState<"profile" | "role" | "overrides" | "assets" | "activity">("profile");
  const [selectedRole, setSelectedRole] = useState<string>("client");
  const [selectedPlan, setSelectedPlan] = useState<string>("free");
  const [status, setStatus] = useState<"active" | "suspended" | "pending">("active");
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setSelectedRole(user.role);
      setSelectedPlan(user.plan || "free");
      setStatus(user.status);
      setOverrides(user.permission_overrides || {});
    }
  }, [user]);

  if (!isOpen || !user) return null;

  const isSelf = user.email.toLowerCase() === currentAdminEmail?.toLowerCase();
  const isProtected = user.is_protected;

  const handleSavePlan = async () => {
    try {
      setIsSaving(true);
      await updateAccessUserPlan(user.id, selectedPlan);
      toast.success(`User plan updated to ${selectedPlan}`);
      onUserUpdated();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to update plan");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveRole = async () => {
    if (isProtected) {
      toast.error("Super Admin / Protected users cannot be modified");
      return;
    }
    if (isSelf) {
      toast.error("You cannot change your own role");
      return;
    }
    try {
      setIsSaving(true);
      await updateAccessUserRole(user.id, selectedRole);
      toast.success(`Role updated to ${selectedRole.toUpperCase()}`);
      onUserUpdated();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to update role");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveStatus = async () => {
    if (isProtected) {
      toast.error("Super Admin / Protected users cannot be modified");
      return;
    }
    if (isSelf) {
      toast.error("You cannot suspend your own account");
      return;
    }
    try {
      setIsSaving(true);
      await updateAccessUserStatus(user.id, status);
      toast.success(`User status updated to ${status}`);
      onUserUpdated();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to update status");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveOverrides = async () => {
    try {
      setIsSaving(true);
      await updateAccessUserOverrides(user.id, overrides);
      toast.success("Permission overrides updated");
      onUserUpdated();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to update overrides");
    } finally {
      setIsSaving(false);
    }
  };

  const toggleOverride = (featId: string) => {
    setOverrides((prev) => {
      const next = { ...prev };
      if (next[featId] === undefined) {
        next[featId] = true;
      } else if (next[featId] === true) {
        next[featId] = false;
      } else {
        delete next[featId];
      }
      return next;
    });
  };

  function initials(name: string): string {
    return name
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? "")
      .join("");
  }

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div className="drawer-panel" onClick={(e) => e.stopPropagation()}>
        {/* Drawer Header */}
        <div className="drawer-header">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              className="user-avatar"
              style={{
                background: "rgba(0,102,224,0.2)",
                color: "#818cf8",
                width: 42,
                height: 42,
              }}
            >
              {initials(user.name)}
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <h3 className="drawer-title">{user.name}</h3>
                {isProtected && <span className="protected-badge">PROTECTED</span>}
                {user.has_custom_overrides && <span className="custom-perms-badge">CUSTOM PERMS</span>}
              </div>
              <span style={{ fontSize: "0.8rem", color: "var(--color-text-muted)" }}>{user.email}</span>
            </div>
          </div>
          <button className="drawer-close-btn" onClick={onClose} aria-label="Close drawer">
            <X size={20} />
          </button>
        </div>

        {/* Drawer Tabs */}
        <div className="drawer-tabs">
          <button
            className={`drawer-tab${activeTab === "profile" ? " active" : ""}`}
            onClick={() => setActiveTab("profile")}
          >
            Profile
          </button>
          <button
            className={`drawer-tab${activeTab === "role" ? " active" : ""}`}
            onClick={() => setActiveTab("role")}
          >
            Role
          </button>
          <button
            className={`drawer-tab${activeTab === "overrides" ? " active" : ""}`}
            onClick={() => setActiveTab("overrides")}
          >
            Overrides
          </button>
          <button
            className={`drawer-tab${activeTab === "assets" ? " active" : ""}`}
            onClick={() => setActiveTab("assets")}
          >
            Assets ({user.assets_count})
          </button>
          <button
            className={`drawer-tab${activeTab === "activity" ? " active" : ""}`}
            onClick={() => setActiveTab("activity")}
          >
            Activity
          </button>
        </div>

        {/* Drawer Body */}
        <div className="drawer-body">
          {/* PROFILE TAB */}
          {activeTab === "profile" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label className="kpi-label">Account ID</label>
                <div style={{ fontSize: "0.85rem", fontFamily: "var(--font-mono)", color: "var(--color-text)" }}>
                  {user.id}
                </div>
              </div>
              <div>
                <label className="kpi-label">Plan Tier</label>
                <select
                  className="select-filter"
                  style={{ width: "100%", marginTop: 4 }}
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                  disabled={isProtected}
                >
                  <option value="free">Free</option>
                  <option value="Business Pro">Business Pro</option>
                  <option value="Enterprise">Enterprise</option>
                </select>
                <button
                  className="btn-primary"
                  style={{ marginTop: 8, width: "100%", justifyContent: "center" }}
                  onClick={handleSavePlan}
                  disabled={isSaving || isProtected}
                >
                  <Save size={14} /> Update Plan
                </button>
              </div>
              <div>
                <label className="kpi-label">Joined Date</label>
                <div style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                  {new Date(user.created_at).toLocaleDateString()}
                </div>
              </div>
              <div>
                <label className="kpi-label">First Product Access</label>
                <div style={{ fontSize: "0.85rem", color: user.first_access_at ? "#10b981" : "var(--color-text-muted)", fontWeight: user.first_access_at ? 600 : 400 }}>
                  {user.first_access_at ? new Date(user.first_access_at).toLocaleString() : "Never (Pending Access)"}
                </div>
              </div>
              <div>
                <label className="kpi-label">Last Successful Login</label>
                <div style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                  {user.last_login ? new Date(user.last_login).toLocaleString() : "Never"}
                </div>
              </div>
              <div>
                <label className="kpi-label">Account Status</label>
                <select
                  className="select-filter"
                  style={{ width: "100%", marginTop: 4 }}
                  value={status}
                  onChange={(e) => setStatus(e.target.value as any)}
                  disabled={isProtected || isSelf}
                >
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="pending">Pending Invite</option>
                </select>
                <button
                  className="btn-primary"
                  style={{ marginTop: 12, width: "100%", justifyContent: "center" }}
                  onClick={handleSaveStatus}
                  disabled={isSaving || isProtected || isSelf}
                >
                  <Save size={14} /> Update Status
                </button>
              </div>
            </div>
          )}

          {/* ROLE TAB */}
          {activeTab === "role" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label className="kpi-label">Current Role</label>
                <select
                  className="select-filter"
                  style={{ width: "100%", marginTop: 4 }}
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  disabled={isProtected || isSelf}
                >
                  <option value="client">Client</option>
                  <option value="analyst">Analyst</option>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>
              <div style={{ background: "var(--color-bg)", padding: 12, borderRadius: 8, fontSize: "0.8rem", color: "var(--color-text-muted)" }}>
                {selectedRole === "client" && "Clients have access to their company's darkweb feed, alerts, customer checks, and subscription."}
                {selectedRole === "analyst" && "Analysts can run malware sample analysis, domain scans, and investigate darknet leaks."}
                {selectedRole === "admin" && "Admins have full operational control over users, scraper monitoring, audit logs, and system configuration."}
                {selectedRole === "super_admin" && "Super Admins have unrestricted system access and protected governance."}
              </div>
              <button
                className="btn-primary"
                style={{ width: "100%", justifyContent: "center" }}
                onClick={handleSaveRole}
                disabled={isSaving || isProtected || isSelf}
              >
                <Save size={14} /> Save Role Changes
              </button>
            </div>
          )}

          {/* OVERRIDES TAB */}
          {activeTab === "overrides" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", margin: 0 }}>
                Set per-user feature permission overrides. Explicit overrides take precedence over the role default matrix.
              </p>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 400, overflowY: "auto" }}>
                {ALL_FEATURES.map((feat) => {
                  const state = overrides[feat.id];
                  return (
                    <div
                      key={feat.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: 8,
                        borderRadius: 6,
                        background: "var(--color-bg)",
                        border: "1px solid var(--color-border)",
                      }}
                    >
                      <div>
                        <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--color-text)" }}>
                          {feat.label}
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }}>{feat.desc}</div>
                      </div>
                      <button
                        className="btn-action"
                        style={{
                          background:
                            state === true
                              ? "rgba(34, 197, 94, 0.2)"
                              : state === false
                              ? "rgba(239, 68, 68, 0.2)"
                              : "transparent",
                          color: state === true ? "#22c55e" : state === false ? "#ef4444" : "var(--color-text-muted)",
                        }}
                        onClick={() => toggleOverride(feat.id)}
                      >
                        {state === true ? "ENABLED" : state === false ? "DISABLED" : "DEFAULT"}
                      </button>
                    </div>
                  );
                })}
              </div>
              <button
                className="btn-primary"
                style={{ width: "100%", justifyContent: "center", marginTop: 8 }}
                onClick={handleSaveOverrides}
                disabled={isSaving}
              >
                <Save size={14} /> Save Overrides
              </button>
            </div>
          )}

          {/* ASSETS TAB */}
          {activeTab === "assets" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span className="kpi-label">Monitored Assets ({user.assets_count})</span>
              </div>
              {user.assets_count === 0 ? (
                <div style={{ padding: 20, textAlign: "center", color: "var(--color-text-muted)", fontSize: "0.85rem" }}>
                  No monitored domains or assets assigned yet.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      style={{
                        padding: 10,
                        borderRadius: 6,
                        background: "var(--color-bg)",
                        border: "1px solid var(--color-border)",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                      }}
                    >
                      <Package size={16} color="var(--color-primary)" />
                      <div>
                        <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--color-text)" }}>
                          {user.email.split("@")[1] || "domain.com"} (Asset #{i})
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }}>
                          Active monitoring · 100% domain coverage
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ACTIVITY TAB */}
          {activeTab === "activity" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <span className="kpi-label">Audit Log History</span>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <div
                  style={{
                    padding: 10,
                    borderRadius: 6,
                    background: "var(--color-bg)",
                    border: "1px solid var(--color-border)",
                    fontSize: "0.8rem",
                  }}
                >
                  <div style={{ fontWeight: 600, color: "var(--color-text)" }}>User created</div>
                  <div style={{ color: "var(--color-text-muted)" }}>{new Date(user.created_at).toLocaleString()}</div>
                </div>
                {user.first_access_at && (
                  <div
                    style={{
                      padding: 10,
                      borderRadius: 6,
                      background: "var(--color-bg)",
                      border: "1px solid rgba(16, 185, 129, 0.3)",
                      fontSize: "0.8rem",
                    }}
                  >
                    <div style={{ fontWeight: 600, color: "#10b981" }}>First successful product access</div>
                    <div style={{ color: "var(--color-text-muted)" }}>{new Date(user.first_access_at).toLocaleString()}</div>
                  </div>
                )}
                {user.last_login && (
                  <div
                    style={{
                      padding: 10,
                      borderRadius: 6,
                      background: "var(--color-bg)",
                      border: "1px solid var(--color-border)",
                      fontSize: "0.8rem",
                    }}
                  >
                    <div style={{ fontWeight: 600, color: "var(--color-text)" }}>Last active session</div>
                    <div style={{ color: "var(--color-text-muted)" }}>{new Date(user.last_login).toLocaleString()}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
